
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { checkAndClearCacheIfNeeded } from "@/utils/cacheManager";
import { navItems } from "./nav-items";
import MembershipSuccess from "./pages/MembershipSuccess";
import PublicReview from "./pages/PublicReview";
import ProtectedRoute from "./components/ProtectedRoute";
import RootRoute from "./components/RootRoute";
import { useEffect } from "react";

// Hide global toasts when in the mobile app shell (/app/*)
const ConditionalToasters = () => {
  const location = useLocation();
  if (location.pathname.startsWith("/app")) return null;
  return (
    <>
      <Toaster />
      <Sonner />
    </>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * (attemptIndex + 1), 2000), // 1s, 2s, 2s max
      refetchOnWindowFocus: false,
      networkMode: 'online',
    },
  },
});

// Make queryClient globally available for cache management
(window as any).reactQueryClient = queryClient;

const App = () => {
  useEffect(() => {
    // Check and clear cache if app version has changed
    const cacheCleared = checkAndClearCacheIfNeeded();
    if (cacheCleared) {
      console.log("App cache cleared due to version update");
    }
  }, []);

  return (
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <BrowserRouter>
          <ConditionalToasters />
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/membership-success" element={<MembershipSuccess />} />
            <Route path="/review/:token" element={<PublicReview />} />
            {/* Handle the specific case where someone tries to access /booking with just one ID */}
            <Route path="/booking/:bookingId" element={<div className="min-h-screen flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <h1 className="text-2xl font-bold mb-4">Invalid Booking URL</h1>
                <p className="text-gray-600 mb-4">This URL appears to be for viewing a booking, not creating one.</p>
                <a href="/dashboard" className="text-blue-500 hover:text-blue-700 underline">
                  Go to Dashboard to view your bookings
                </a>
              </div>
            </div>} />
            {navItems.map(({ to, page }) => {
              // Protect all management routes
              if (to.startsWith('/management')) {
                return (
                  <Route 
                    key={to} 
                    path={to} 
                    element={
                      <ProtectedRoute>
                        {page}
                      </ProtectedRoute>
                    } 
                  />
                );
              }
              // Regular routes
              return <Route key={to} path={to} element={page} />;
            })}
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
